import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../context/AuthContext';
import { createCampaign } from '../../services/donationCampaignService';
import CampaignBasicFields from './CampaignBasicFields';
import CampaignBeneficiaryFields from './CampaignBeneficiaryFields';
import CampaignSuccessBanner from './CampaignSuccessBanner';

const { FiX, FiChevronRight, FiChevronLeft, FiHeart, FiCheck } = FiIcons;

const STEPS = ['Campaign Details', 'Beneficiary', 'Review'];

const DonationCampaignForm = ({ memorialId, memorialName, onSuccess, onCancel }) => {
  const { user } = useAuth();
  const [step,       setStep]       = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');
  const [created,    setCreated]    = useState(null);

  const [form, setForm] = useState({
    title:               '',
    description:         '',
    goal_amount:         '',
    deadline:            '',
    cover_image_url:     '',
    beneficiary_type:    '',
    beneficiary_name:    '',
    beneficiary_details: {},
  });

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const validateStep = () => {
    setError('');
    if (step === 0) {
      if (!form.title.trim())       { setError('Campaign title is required.'); return false; }
      if (!form.description.trim()) { setError('Description is required.'); return false; }
      if (!form.goal_amount || Number(form.goal_amount) <= 0) { setError('A valid fundraising goal is required.'); return false; }
      if (!form.deadline)           { setError('Deadline is required.'); return false; }
      if (new Date(form.deadline) <= new Date()) { setError('Deadline must be a future date.'); return false; }
    }
    if (step === 1) {
      if (!form.beneficiary_type)        { setError('Please select a beneficiary type.'); return false; }
      if (!form.beneficiary_name.trim()) { setError('Beneficiary name is required.'); return false; }
    }
    return true;
  };

  const handleNext = () => { if (validateStep()) setStep(s => s + 1); };
  const handleBack = () => { setError(''); setStep(s => s - 1); };

  const handleSubmit = useCallback(async () => {
    setError('');
    setSubmitting(true);
    try {
      const result = await createCampaign({
        owner_id:            user?.id || 'guest',
        title:               form.title.trim(),
        description:         form.description.trim(),
        goal_amount:         Number(form.goal_amount),
        deadline:            form.deadline,
        memorial_id:         memorialId  || '',
        memorial_name:       memorialName || '',
        beneficiary_type:    form.beneficiary_type,
        beneficiary_name:    form.beneficiary_name.trim(),
        beneficiary_details: form.beneficiary_details,
        cover_image_url:     form.cover_image_url.trim(),
      });
      setCreated(result);
      onSuccess?.(result);
    } catch (err) {
      setError(err.message || 'Failed to create campaign. Please try again.');
    }
    setSubmitting(false);
  }, [form, user, memorialId, memorialName, onSuccess]);

  if (created) return <CampaignSuccessBanner campaign={form} result={created} onClose={onCancel} />;

  return (
    <div className="bg-white rounded-[32px] border border-brand-dark shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-accent-navy to-[#1a2a5e] p-6 flex items-center justify-between">
        <div>
          <h2 className="font-serif text-xl text-white">Create Donation Campaign</h2>
          {memorialName && <p className="text-white/50 text-[10px] font-black uppercase tracking-widest mt-0.5">In memory of {memorialName}</p>}
        </div>
        <button onClick={onCancel} className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/60 hover:text-white">
          <SafeIcon icon={FiX} className="w-5 h-5" />
        </button>
      </div>

      {/* Steps */}
      <div className="flex border-b border-brand-light">
        {STEPS.map((label, i) => (
          <div key={i} className={`flex-1 py-3 text-center text-[9px] font-black uppercase tracking-widest transition-all ${
            step === i ? 'text-accent-navy border-b-2 border-accent-gold' : step > i ? 'text-accent-gold' : 'text-text-muted'
          }`}>
            {step > i ? <SafeIcon icon={FiCheck} className="inline w-3 h-3 mr-1" /> : null}{label}
          </div>
        ))}
      </div>

      {/* Scrollable body */}
      <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
        <div className="p-6">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="step0" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}>
                <CampaignBasicFields form={form} update={update} />
              </motion.div>
            )}
            {step === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}>
                <CampaignBeneficiaryFields form={form} update={update} />
              </motion.div>
            )}
            {step === 2 && (
              <motion.div key="step2" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}>
                <ReviewStep form={form} memorialName={memorialName} />
              </motion.div>
            )}
          </AnimatePresence>

          {error && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              {error}
            </motion.p>
          )}
        </div>
      </div>

      {/* Fixed footer */}
      <div className="flex gap-3 p-6 pt-4 border-t border-brand-light bg-white">
        {step > 0 && (
          <button onClick={handleBack}
            className="px-5 py-3.5 rounded-2xl border-2 border-brand-dark text-[10px] font-black uppercase tracking-widest text-text-muted hover:border-accent-gold transition-all flex items-center gap-1.5">
            <SafeIcon icon={FiChevronLeft} className="w-3.5 h-3.5" /> Back
          </button>
        )}
        {step < 2 ? (
          <button onClick={handleNext}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-accent-navy text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-lg shadow-accent-navy/20">
            Continue <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={handleSubmit} disabled={submitting}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-accent-gold text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold/90 transition-all shadow-lg shadow-accent-gold/30 disabled:opacity-50">
            {submitting
              ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              : <><SafeIcon icon={FiHeart} className="w-4 h-4" /> Launch Campaign</>
            }
          </button>
        )}
      </div>
    </div>
  );
};

const ReviewStep = ({ form, memorialName }) => (
  <div className="space-y-4">
    <h3 className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-4">Review Your Campaign</h3>
    <div className="bg-brand-light/50 border border-brand-dark rounded-3xl divide-y divide-brand-dark overflow-hidden">
      {[
        ['Title',        form.title],
        ['Goal',         `$${Number(form.goal_amount).toLocaleString()}`],
        ['Deadline',     form.deadline],
        ['Beneficiary',  `${form.beneficiary_type?.replace('_', ' ')} — ${form.beneficiary_name}`],
        ['Memorial',     memorialName || 'Not linked'],
      ].map(([k, v]) => (
        <div key={k} className="flex justify-between items-center px-5 py-3">
          <span className="text-[10px] font-black uppercase tracking-widest text-text-muted">{k}</span>
          <span className="text-sm font-bold text-accent-navy max-w-[60%] text-right">{v}</span>
        </div>
      ))}
    </div>
    {form.description && (
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl">
        <p className="text-[10px] font-black uppercase tracking-widest text-amber-700 mb-1">Description</p>
        <p className="text-sm text-amber-800 leading-relaxed">{form.description}</p>
      </div>
    )}
    <p className="text-center text-[10px] text-text-muted">
      Your campaign will be live immediately. A super admin can feature it on the home page.
    </p>
  </div>
);

export default DonationCampaignForm;